/**
 * Permissions Hook
 * Granular permission checking based on user role and permissions
 *
 * SUPER_ADMIN always has full access (hardcoded bypass).
 * ADMIN uses permissions from JWT / store, falling back to DEFAULT_PERMISSIONS.
 */

import { useMemo } from 'react';
import { useAuthStore } from '@/store/useAuthStore';

// ============================================
// PERMISSIONS CONSTANTS (mirrored from backend)
// ============================================

export const PERMISSIONS = {
  // Members
  MEMBERS_READ: 'members.read',
  MEMBERS_WRITE: 'members.write',
  MEMBERS_DELETE: 'members.delete',
  MEMBERS_EXPORT: 'members.export',

  // Blog
  BLOG_READ: 'blog.read',
  BLOG_WRITE: 'blog.write',
  BLOG_DELETE: 'blog.delete',

  // Donations
  DONATIONS_READ: 'donations.read',
  DONATIONS_WRITE: 'donations.write',
  DONATIONS_DELETE: 'donations.delete',
  DONATIONS_EXPORT: 'donations.export',

  // Events
  EVENTS_READ: 'events.read',
  EVENTS_WRITE: 'events.write',
  EVENTS_DELETE: 'events.delete',
  EVENTS_CHECKIN: 'events.checkin',

  // Contacts
  CONTACTS_READ: 'contacts.read',
  CONTACTS_WRITE: 'contacts.write',
  CONTACTS_DELETE: 'contacts.delete',

  // Settings
  SETTINGS_READ: 'settings.read',
  SETTINGS_WRITE: 'settings.write',

  // Audit
  AUDIT_READ: 'audit.read',
  AUDIT_EXPORT: 'audit.export',

  // Gallery
  GALLERY_READ: 'gallery.read',
  GALLERY_WRITE: 'gallery.write',

  // Users/Admins
  USERS_READ: 'users.read',
  USERS_WRITE: 'users.write',
  USERS_DELETE: 'users.delete',

  // Schools
  SCHOOLS_READ: 'schools.read',
  SCHOOLS_WRITE: 'schools.write',
  SCHOOLS_DELETE: 'schools.delete',

  // Bookings
  BOOKINGS_READ: 'bookings.read',
  BOOKINGS_WRITE: 'bookings.write',
  BOOKINGS_CHECKIN: 'bookings.checkin',

  // Email
  EMAIL_REPLY_TO: 'email.reply_to',

  // YIM Talk
  YIMTALK_READ: 'yimtalk.read',
  YIMTALK_WRITE: 'yimtalk.write',
  YIMTALK_DELETE: 'yimtalk.delete',

  // YIM Meet
  YIMMEET_READ: 'yimmeet.read',
  YIMMEET_WRITE: 'yimmeet.write',
  YIMMEET_DELETE: 'yimmeet.delete',

  // YIM Form
  FORMS_READ: 'forms.read',
  FORMS_WRITE: 'forms.write',
  FORMS_DELETE: 'forms.delete',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/** All possible permissions */
export const ALL_PERMISSIONS: string[] = Object.values(PERMISSIONS);

/** Default permissions per role (used when User.permissions is null/undefined) */
export const DEFAULT_PERMISSIONS: Record<string, string[]> = {
  SUPER_ADMIN: ALL_PERMISSIONS,
  ADMIN: [
    PERMISSIONS.MEMBERS_READ,
    PERMISSIONS.MEMBERS_WRITE,
    PERMISSIONS.MEMBERS_EXPORT,
    PERMISSIONS.BLOG_READ,
    PERMISSIONS.BLOG_WRITE,
    PERMISSIONS.DONATIONS_READ,
    PERMISSIONS.DONATIONS_WRITE,
    PERMISSIONS.DONATIONS_EXPORT,
    PERMISSIONS.EVENTS_READ,
    PERMISSIONS.EVENTS_WRITE,
    PERMISSIONS.CONTACTS_READ,
    PERMISSIONS.CONTACTS_WRITE,
    PERMISSIONS.SETTINGS_READ,
    PERMISSIONS.AUDIT_READ,
    PERMISSIONS.GALLERY_READ,
    PERMISSIONS.GALLERY_WRITE,
    PERMISSIONS.USERS_READ,
    PERMISSIONS.SCHOOLS_READ,
    PERMISSIONS.SCHOOLS_WRITE,
    PERMISSIONS.BOOKINGS_READ,
    PERMISSIONS.BOOKINGS_WRITE,
    PERMISSIONS.FORMS_READ,
    PERMISSIONS.FORMS_WRITE,
  ],
  USER: [],
};

/**
 * Permission modules for the admin UI grid
 * Groups permissions by module for display
 */
export const PERMISSION_MODULES = [
  {
    key: 'members',
    labelFr: 'Membres',
    labelEn: 'Members',
    permissions: ['members.read', 'members.write', 'members.delete', 'members.export'],
  },
  {
    key: 'blog',
    labelFr: 'Blog',
    labelEn: 'Blog',
    permissions: ['blog.read', 'blog.write', 'blog.delete'],
  },
  {
    key: 'donations',
    labelFr: 'Donations',
    labelEn: 'Donations',
    permissions: ['donations.read', 'donations.write', 'donations.delete', 'donations.export'],
  },
  {
    key: 'events',
    labelFr: 'Événements',
    labelEn: 'Events',
    permissions: ['events.read', 'events.write', 'events.delete', 'events.checkin'],
  },
  {
    key: 'contacts',
    labelFr: 'Contacts',
    labelEn: 'Contacts',
    permissions: ['contacts.read', 'contacts.write', 'contacts.delete'],
  },
  {
    key: 'schools',
    labelFr: 'Écoles',
    labelEn: 'Schools',
    permissions: ['schools.read', 'schools.write', 'schools.delete'],
  },
  {
    key: 'gallery',
    labelFr: 'Galerie',
    labelEn: 'Gallery',
    permissions: ['gallery.read', 'gallery.write'],
  },
  {
    key: 'settings',
    labelFr: 'Paramètres',
    labelEn: 'Settings',
    permissions: ['settings.read', 'settings.write', 'email.reply_to'],
  },
  {
    key: 'audit',
    labelFr: 'Audit',
    labelEn: 'Audit',
    permissions: ['audit.read', 'audit.export'],
  },
  {
    key: 'users',
    labelFr: 'Admins',
    labelEn: 'Admins',
    permissions: ['users.read', 'users.write', 'users.delete'],
  },
  {
    key: 'bookings',
    labelFr: 'Réservations',
    labelEn: 'Bookings',
    permissions: ['bookings.read', 'bookings.write', 'bookings.checkin'],
  },
  {
    key: 'yimtalk',
    labelFr: 'YIM Talk',
    labelEn: 'YIM Talk',
    permissions: ['yimtalk.read', 'yimtalk.write', 'yimtalk.delete'],
  },
  {
    key: 'yimmeet',
    labelFr: 'YIM Meet',
    labelEn: 'YIM Meet',
    permissions: ['yimmeet.read', 'yimmeet.write', 'yimmeet.delete'],
  },
  {
    key: 'forms',
    labelFr: 'YIM Form',
    labelEn: 'YIM Form',
    permissions: ['forms.read', 'forms.write', 'forms.delete'],
  },
] as const;

// ============================================
// PERMISSION HELPERS
// ============================================

export function canReadRegistrations(has: (perm: string) => boolean): boolean {
  return (
    has(PERMISSIONS.EVENTS_READ) ||
    has(PERMISSIONS.EVENTS_WRITE) ||
    has(PERMISSIONS.EVENTS_CHECKIN)
  );
}

export function canCheckInRegistrations(has: (perm: string) => boolean): boolean {
  return has(PERMISSIONS.EVENTS_WRITE) || has(PERMISSIONS.EVENTS_CHECKIN);
}

export function canManageEvents(has: (perm: string) => boolean): boolean {
  return has(PERMISSIONS.EVENTS_WRITE) || has(PERMISSIONS.EVENTS_DELETE);
}

export function isEventsCheckInOnly(has: (perm: string) => boolean): boolean {
  return (
    has(PERMISSIONS.EVENTS_CHECKIN) &&
    !has(PERMISSIONS.EVENTS_READ) &&
    !has(PERMISSIONS.EVENTS_WRITE) &&
    !has(PERMISSIONS.EVENTS_DELETE)
  );
}

export function canReadBookings(has: (perm: string) => boolean): boolean {
  return (
    has(PERMISSIONS.BOOKINGS_READ) ||
    has(PERMISSIONS.BOOKINGS_WRITE) ||
    has(PERMISSIONS.BOOKINGS_CHECKIN)
  );
}

export function canCheckInBookings(has: (perm: string) => boolean): boolean {
  return has(PERMISSIONS.BOOKINGS_WRITE) || has(PERMISSIONS.BOOKINGS_CHECKIN);
}

/** Approve, reject, check-in, export — everything except delete */
export function canOperateBookings(has: (perm: string) => boolean): boolean {
  return has(PERMISSIONS.BOOKINGS_WRITE) || has(PERMISSIONS.BOOKINGS_CHECKIN);
}

/** Delete only — full bookings.write, not check-in */
export function canDeleteBookings(has: (perm: string) => boolean): boolean {
  return has(PERMISSIONS.BOOKINGS_WRITE);
}

/** @deprecated Use canOperateBookings */
export function canManageBookings(has: (perm: string) => boolean): boolean {
  return canOperateBookings(has);
}

export function isBookingsCheckInOnly(has: (perm: string) => boolean): boolean {
  return (
    has(PERMISSIONS.BOOKINGS_CHECKIN) &&
    !has(PERMISSIONS.BOOKINGS_READ) &&
    !has(PERMISSIONS.BOOKINGS_WRITE)
  );
}

// ============================================
// HOOK
// ============================================

export interface UsePermissionsReturn {
  /** Check if the current user has a specific permission */
  hasPermission: (perm: string) => boolean;
  /** Check if the current user can access a module (has any permission for it) */
  canAccess: (module: string) => boolean;
  /** The resolved list of permissions for the current user */
  permissions: string[];
  /** The current user's role */
  role: string | undefined;
}

/**
 * Hook for granular permission checking
 *
 * SUPER_ADMIN always returns true for hasPermission and canAccess.
 * If user.permissions is null/undefined, falls back to DEFAULT_PERMISSIONS[role].
 * Explicit empty array [] means zero module access (dashboard/notifications/account only).
 */
export function usePermissions(): UsePermissionsReturn {
  const user = useAuthStore((state) => state.user);

  const role = user?.role;
  const isSuperAdmin = role === 'SUPER_ADMIN';

  // Resolve permissions: null/undefined → role defaults; [] → zero access
  const permissions = useMemo(() => {
    if (!role) return [];
    if (isSuperAdmin) return ALL_PERMISSIONS;

    const userPerms = user?.permissions;
    if (Array.isArray(userPerms)) {
      return userPerms;
    }

    return DEFAULT_PERMISSIONS[role] ?? [];
  }, [role, isSuperAdmin, user]);

  const permissionsSet = useMemo(() => new Set(permissions), [permissions]);

  const hasPermission = useMemo(
    () => (perm: string): boolean => {
      if (isSuperAdmin) return true;
      return permissionsSet.has(perm);
    },
    [isSuperAdmin, permissionsSet]
  );

  const canAccess = useMemo(
    () => (module: string): boolean => {
      if (isSuperAdmin) return true;
      // Check if user has any permission that starts with "module."
      return permissions.some((p) => p.startsWith(`${module}.`));
    },
    [isSuperAdmin, permissions]
  );

  return { hasPermission, canAccess, permissions, role };
}

export default usePermissions;
